import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, NgZone, effect, inject, signal, untracked } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { matchesRoute } from '../navigation/app-navigation';
import { canStartTour } from './help-access';
import { HelpStateService } from './help-state.service';
import { Localized, Tour, TourStep } from './help.models';

export interface TourRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface ActiveTourStep {
  tour: Tour;
  index: number;
  total: number;
  title: Localized;
  text: Localized;
  rect: TourRect | null;
  isFirst: boolean;
  isLast: boolean;
  /** El recorrido no puede continuar; la burbuja explica el motivo. */
  blocked: boolean;
  /** Acción para corregir el bloqueo (p. ej. ir a Informes), si la pantalla la ofrece. */
  fix?: TourFix;
}

export interface TourFix {
  label: string;
  route: string;
}

/** Motivo que publica la propia pantalla en el elemento señalado (`data-tour-blocked*`), ya traducido. */
export interface TourBlockedReason {
  text: string;
  fix?: TourFix;
}

export function readBlockedReason(el: Element | null): TourBlockedReason | null {
  const host = el?.closest('[data-tour]') ?? el;
  const text = host?.getAttribute('data-tour-blocked')?.trim();
  if (!host || !text) return null;
  const route = host.getAttribute('data-tour-blocked-route')?.trim();
  const label = host.getAttribute('data-tour-blocked-action')?.trim();
  return { text, fix: route && label ? { route, label } : undefined };
}

const TARGET_TIMEOUT_MS = 3500;
/** Sin navegación ni acción previa la pantalla ya está pintada: un target ausente no va a aparecer. */
const SETTLED_TARGET_TIMEOUT_MS = 1000;
const POLL_MS = 120;
const VIEWPORT_MARGIN = 16;
const SCROLL_TIMEOUT_MS = 1500;
const SCROLL_STABLE_FRAMES = 6;

const NOT_FOUND: { title: Localized; text: Localized } = {
  title: { es: 'No pudimos mostrar esta guía', it: 'Non siamo riusciti a mostrare questa guida' },
  text: {
    es: 'Los elementos de esta guía no están disponibles ahora en la pantalla. Puedes leer los pasos en el artículo de Ayuda.',
    it: 'Gli elementi di questa guida non sono disponibili ora sullo schermo. Puoi leggere i passaggi nell\'articolo di Aiuto.',
  },
};

const STOPPED: { title: Localized; text: Localized } = {
  title: { es: 'No se pudo continuar el recorrido', it: 'Non è stato possibile continuare il percorso' },
  text: {
    es: 'La pantalla no abrió lo que esperábamos. Revisa si aparece algún aviso, corrígelo y vuelve a abrir esta guía. También puedes leer los pasos en el artículo de Ayuda.',
    it: 'La schermata non ha aperto quello che ci aspettavamo. Controlla se compare qualche avviso, correggilo e riapri questa guida. Puoi anche leggere i passaggi nell\'articolo di Aiuto.',
  },
};

export function isElementVisible(el: Element): boolean {
  if (!el.isConnected || el.getClientRects().length === 0) return false;
  const style = getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none';
}

/** Botón real de un host PrimeNG (`p-button`) o el propio elemento si ya es interactivo. */
export function interactiveElement(el: Element): HTMLElement | null {
  if (el.matches('button, a, input, select, textarea')) return el as HTMLElement;
  return el.querySelector<HTMLElement>(':scope > button');
}

export function isElementDisabled(el: Element): boolean {
  if (el.getAttribute('aria-disabled') === 'true') return true;
  const control = interactiveElement(el);
  if (!control) return false;
  return (
    (control as HTMLButtonElement).disabled === true ||
    control.getAttribute('aria-disabled') === 'true' ||
    control.classList.contains('p-disabled')
  );
}

export function findTourTarget(doc: Document, name: string): HTMLElement | null {
  const nodes = doc.querySelectorAll<HTMLElement>(`[data-tour="${CSS.escape(name)}"]`);
  for (const node of Array.from(nodes)) {
    if (isElementVisible(node)) return node;
    // Hosts de componentes con `display: contents` no tienen caja propia.
    const child = node.firstElementChild as HTMLElement | null;
    if (child && node.getClientRects().length === 0 && isElementVisible(child)) return child;
  }
  return null;
}

@Injectable({ providedIn: 'root' })
export class TourService {
  private router = inject(Router);
  private state = inject(HelpStateService);
  private announcer = inject(LiveAnnouncer);
  private zone = inject(NgZone);
  private doc = inject(DOCUMENT);

  readonly active = signal<ActiveTourStep | null>(null);

  private runId = 0;
  private stepRunId = 0;
  private currentEl: HTMLElement | null = null;
  private frame = 0;
  private programmaticClick = false;
  private listening = false;

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        const current = this.active();
        if (current && !this.isAllowedRoute(current.tour, e.urlAfterRedirects)) this.pause();
      });

    let firstIdentity = true;
    effect(() => {
      this.state.identityVersion();
      if (firstIdentity) {
        firstIdentity = false;
        return;
      }
      untracked(() => this.stop());
    });

    inject(DestroyRef).onDestroy(() => this.stop());
  }

  /** Único punto de entrada. Solo se llama desde una acción explícita del usuario en el panel de Ayuda. */
  async start(tourId: string, opts: { restart?: boolean } = {}): Promise<boolean> {
    let tour: Tour | undefined;
    try {
      tour = (await this.state.loadContent()).TOURS.find(t => t.id === tourId);
    } catch {
      return false;
    }
    if (!tour || !tour.steps.length || !canStartTour(tour, this.state.context())) return false;

    if (this.active()) this.pause();
    const run = ++this.runId;

    if (!this.isAllowedRoute(tour, this.router.url)) {
      const ok = await this.router.navigateByUrl(tour.route).catch(() => false);
      if (!ok || run !== this.runId) return false;
    }

    const saved = this.state.prefs().tours[tour.id];
    const startAt = !opts.restart && saved && !saved.done ? Math.min(saved.step, tour.steps.length - 1) : 0;

    this.addListeners();
    await this.show(tour, startAt, 1, run);
    return run === this.runId;
  }

  next(): void {
    this.advance(true);
  }

  private advance(activate: boolean): void {
    const current = this.active();
    if (!current) return;
    if (current.isLast) {
      this.finish(!current.blocked);
      return;
    }
    const step = current.tour.steps[current.index];
    // El bloqueo puede llegar después de mostrar el paso (p. ej. datos que cargan tarde).
    if (step.gate && this.currentEl && isElementDisabled(this.currentEl)) {
      this.renderGate(current.tour, current.index, step, this.currentEl);
      return;
    }
    let triggered = !activate;
    if (activate && step.activateOnNext && this.currentEl && !isElementDisabled(this.currentEl)) {
      const control = interactiveElement(this.currentEl) ?? this.currentEl;
      this.programmaticClick = true;
      try {
        control.click();
      } finally {
        this.programmaticClick = false;
      }
      triggered = true;
    }
    void this.show(current.tour, current.index + 1, 1, this.runId, !triggered);
  }

  prev(): void {
    const current = this.active();
    if (!current || current.isFirst || current.blocked) return;
    void this.show(current.tour, current.index - 1, -1, this.runId, true);
  }

  /** Botón de la burbuja bloqueada: termina el recorrido y lleva a la pantalla donde se corrige. */
  applyFix(): void {
    const fix = this.active()?.fix;
    if (!fix) return;
    this.finish(false);
    void this.router.navigateByUrl(fix.route).catch(() => false);
  }

  /** Salir, Escape, navegar fuera o abrir la Ayuda: guarda el paso para poder continuar. */
  pause(): void {
    const current = this.active();
    if (current && !current.blocked) {
      this.saveProgress(current.tour.id, current.index, false);
    }
    this.stop();
  }

  private finish(done: boolean): void {
    const current = this.active();
    if (current) {
      if (done) {
        this.saveProgress(current.tour.id, 0, true);
      } else {
        this.state.updatePrefs(prefs => {
          const tours = { ...prefs.tours };
          delete tours[current.tour.id];
          return { ...prefs, tours };
        });
      }
    }
    this.stop();
  }

  private stop(): void {
    this.runId++;
    this.stepRunId++;
    this.currentEl = null;
    this.active.set(null);
    this.removeListeners();
  }

  private async show(tour: Tour, from: number, direction: 1 | -1, run: number, settled = false): Promise<void> {
    const stepRun = ++this.stepRunId;
    const alive = () => run === this.runId && stepRun === this.stepRunId;
    const shownAny = this.active() !== null;
    /** El paso anterior pulsó algo (abrir formulario, navegar) y se espera que aparezca lo siguiente. */
    const afterTrigger = shownAny && direction === 1 && !settled;
    const previousEl = this.currentEl;

    for (let i = from; i >= 0 && i < tour.steps.length; i += direction) {
      const step = tour.steps[i];

      if (!step.target) {
        this.render(tour, i, step, null);
        return;
      }

      const stepRoute = step.route ?? tour.route;
      if (!matchesRoute(this.router.url, stepRoute)) {
        settled = false;
        const ok = await this.router.navigateByUrl(stepRoute).catch(() => false);
        if (!alive()) return;
        if (!ok) break;
      }

      const timeout = settled ? SETTLED_TARGET_TIMEOUT_MS : TARGET_TIMEOUT_MS;
      const el = await this.waitForTarget(step.target, alive, timeout);
      if (!alive()) return;

      const usable = !!el && !isElementDisabled(el);
      if (!usable) {
        if (step.gate) {
          this.renderGate(tour, i, step, el);
          return;
        }
        // Ya se dio tiempo a la pantalla: el resto de pasos ausentes no deben acumular esperas largas.
        if (!el) settled = true;
        continue;
      }

      await this.ensureInView(el!, alive);
      if (!alive()) return;
      this.render(tour, i, step, el!);
      return;
    }

    if (!alive()) return;
    if (direction === -1) return;
    if (afterTrigger) {
      const reason = readBlockedReason(previousEl);
      this.renderBlocked(tour, from, STOPPED.title, reason ? this.same(reason.text) : STOPPED.text, reason?.fix);
    } else if (shownAny) {
      this.finish(true);
    } else {
      this.renderBlocked(tour, from, NOT_FOUND.title, NOT_FOUND.text);
    }
  }

  private render(tour: Tour, index: number, step: TourStep, el: HTMLElement | null): void {
    this.currentEl = el;
    const next: ActiveTourStep = {
      tour,
      index,
      total: tour.steps.length,
      title: step.title,
      text: step.text,
      rect: el ? this.measure(el) : null,
      isFirst: index === 0,
      isLast: index === tour.steps.length - 1,
      blocked: false,
    };
    this.active.set(next);
    this.saveProgress(tour.id, index, false);
    this.announce(next);
  }

  /** Bloqueo de un paso con `gate`: usa el motivo que publique la pantalla y, si no hay, el texto fijo. */
  private renderGate(tour: Tour, index: number, step: TourStep, el: HTMLElement | null): void {
    const gate = step.gate!;
    const reason = readBlockedReason(el);
    this.renderBlocked(tour, index, gate.blockedTitle, reason ? this.same(reason.text) : gate.blockedText, reason?.fix);
  }

  /** Un texto que la pantalla ya tradujo al idioma activo sirve para ambos idiomas. */
  private same(text: string): Localized {
    return { es: text, it: text };
  }

  private renderBlocked(tour: Tour, index: number, title: Localized, text: Localized, fix?: TourFix): void {
    this.currentEl = null;
    const next: ActiveTourStep = {
      tour,
      index,
      total: tour.steps.length,
      title,
      text,
      rect: null,
      isFirst: true,
      isLast: true,
      blocked: true,
      fix,
    };
    this.active.set(next);
    this.announce(next);
  }

  private announce(step: ActiveTourStep): void {
    const lang = this.state.lang();
    const progress = step.blocked
      ? ''
      : lang === 'it'
        ? ` Passo ${step.index + 1} di ${step.total}.`
        : ` Paso ${step.index + 1} de ${step.total}.`;
    void this.announcer.announce(`${step.title[lang]}.${progress} ${step.text[lang]}`, 'polite');
  }

  private saveProgress(tourId: string, step: number, done: boolean): void {
    this.state.updatePrefs(prefs => ({ ...prefs, tours: { ...prefs.tours, [tourId]: { step, done } } }));
  }

  private isAllowedRoute(tour: Tour, url: string): boolean {
    return [tour.route, ...(tour.extraRoutes ?? [])].some(route => matchesRoute(url, route));
  }

  private async waitForTarget(name: string, alive: () => boolean, timeoutMs = TARGET_TIMEOUT_MS): Promise<HTMLElement | null> {
    const deadline = Date.now() + timeoutMs;
    while (alive()) {
      const el = findTourTarget(this.doc, name);
      if (el) return el;
      if (Date.now() >= deadline) return null;
      await new Promise(resolve => setTimeout(resolve, POLL_MS));
    }
    return null;
  }

  private async ensureInView(el: HTMLElement, alive: () => boolean): Promise<void> {
    const view = this.doc.defaultView;
    if (!view || this.isComfortablyVisible(el)) return;
    const reduced = view.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    this.scrollToTarget(el, reduced ? 'auto' : 'smooth');
    await this.waitForScrollToSettle(el, alive);
    if (!alive() || this.isComfortablyVisible(el)) return;

    // El desplazamiento suave puede quedarse a medias si algo lo interrumpe.
    this.scrollToTarget(el, 'auto');
    await this.nextFrame();
  }

  /** Visible y fuera de barras fijas o pegajosas (p. ej. la barra superior de la app). */
  private isComfortablyVisible(el: HTMLElement): boolean {
    const view = this.doc.defaultView;
    if (!view) return true;
    const rect = el.getBoundingClientRect();
    const top = this.topInset(el) + VIEWPORT_MARGIN;
    const bottom = view.innerHeight - VIEWPORT_MARGIN;
    const horizontal = rect.left >= 0 && rect.right <= view.innerWidth;
    if (rect.height > bottom - top) {
      // Elemento más alto que la pantalla: basta con que empiece en la zona visible.
      return horizontal && rect.top >= top && rect.top <= top + (bottom - top) / 3;
    }
    return horizontal && rect.top >= top && rect.bottom <= bottom;
  }

  private scrollToTarget(el: HTMLElement, behavior: ScrollBehavior): void {
    const view = this.doc.defaultView;
    if (!view) return;
    const inset = this.topInset(el);
    const available = view.innerHeight - inset - VIEWPORT_MARGIN * 2;
    const block: ScrollLogicalPosition = el.getBoundingClientRect().height > available ? 'start' : 'center';
    const previous = el.style.scrollMarginTop;
    // Con `center`, un margen superior igual a la barra centra el elemento en el hueco libre.
    el.style.scrollMarginTop = `${block === 'start' ? inset + VIEWPORT_MARGIN : inset}px`;
    el.scrollIntoView({ block, inline: 'nearest', behavior });
    el.style.scrollMarginTop = previous;
  }

  /** Alto de la barra fija o pegajosa que tapa la parte superior, salvo que contenga al target. */
  private topInset(el: HTMLElement): number {
    const view = this.doc.defaultView;
    if (!view) return 0;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(rect.left + rect.width / 2, 1), view.innerWidth - 1);
    for (const node of this.doc.elementsFromPoint(x, 1)) {
      if (node.closest('app-tour-overlay, app-help-center')) continue;
      const bar = this.pinnedAncestor(node);
      if (!bar || bar.contains(el)) return 0;
      const bottom = bar.getBoundingClientRect().bottom;
      return bottom > 0 && bottom < view.innerHeight / 2 ? bottom : 0;
    }
    return 0;
  }

  private pinnedAncestor(node: Element): Element | null {
    for (let current: Element | null = node; current && current !== this.doc.body; current = current.parentElement) {
      const position = getComputedStyle(current).position;
      if (position === 'fixed' || position === 'sticky') return current;
    }
    return null;
  }

  /** Espera a que el target deje de moverse (fin del scroll suave) o a un máximo de tiempo. */
  private async waitForScrollToSettle(el: HTMLElement, alive: () => boolean): Promise<void> {
    const deadline = Date.now() + SCROLL_TIMEOUT_MS;
    let lastTop = el.getBoundingClientRect().top;
    let stableFrames = 0;
    while (alive() && Date.now() < deadline && stableFrames < SCROLL_STABLE_FRAMES) {
      await this.nextFrame();
      const top = el.getBoundingClientRect().top;
      stableFrames = Math.abs(top - lastTop) < 0.5 ? stableFrames + 1 : 0;
      lastTop = top;
    }
  }

  private nextFrame(): Promise<void> {
    return new Promise(resolve => requestAnimationFrame(() => resolve()));
  }

  private measure(el: HTMLElement): TourRect | null {
    if (!el.isConnected) return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }

  private reposition = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      const current = this.active();
      if (!current) return;
      const rect = this.currentEl ? this.measure(this.currentEl) : null;
      this.zone.run(() => this.active.set({ ...current, rect }));
    });
  };

  private onDocumentClick = (event: MouseEvent) => {
    if (this.programmaticClick) return;
    const current = this.active();
    if (!current || !this.currentEl) return;
    const step = current.tour.steps[current.index];
    if (!step.clickAdvances || current.blocked) return;
    if (!(event.target instanceof Node) || !this.currentEl.contains(event.target)) return;
    const el = this.currentEl;
    if (isElementDisabled(el)) {
      if (step.gate) setTimeout(() => this.zone.run(() => this.renderGate(current.tour, current.index, step, el)), 0);
      return;
    }
    setTimeout(() => this.zone.run(() => this.advance(false)), 0);
  };

  private onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.active()) {
      this.zone.run(() => this.pause());
    }
  };

  private addListeners(): void {
    if (this.listening) return;
    this.listening = true;
    const view = this.doc.defaultView;
    this.zone.runOutsideAngular(() => {
      view?.addEventListener('resize', this.reposition, { passive: true });
      this.doc.addEventListener('scroll', this.reposition, { passive: true, capture: true });
      this.doc.addEventListener('click', this.onDocumentClick, true);
      this.doc.addEventListener('keydown', this.onKeydown, true);
    });
  }

  private removeListeners(): void {
    if (!this.listening) return;
    this.listening = false;
    const view = this.doc.defaultView;
    view?.removeEventListener('resize', this.reposition);
    this.doc.removeEventListener('scroll', this.reposition, true);
    this.doc.removeEventListener('click', this.onDocumentClick, true);
    this.doc.removeEventListener('keydown', this.onKeydown, true);
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }
}
